import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  CellText,
  Flex,
  Table,
  Text,
  useTable,
  type ColumnConfig,
} from '@backstage/ui';
import {
  RateLimitResource,
  RateLimitResponse,
  useGitHubRateLimit,
} from './useGitHubRateLimit';

interface RateLimitRow extends RateLimitResource {
  id: string;
  name: string;
}

function formatResetTime(unixTimestamp: number): string {
  return new Date(unixTimestamp * 1000).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

const RESOURCE_DISPLAY_ORDER = [
  'core',
  'search',
  'graphql',
  'code_search',
  'integration_manifest',
  'source_import',
];

const columns: ColumnConfig<RateLimitRow>[] = [
  {
    id: 'name',
    label: 'Resource',
    isRowHeader: true,
    cell: row => <CellText title={row.name} />,
  },
  {
    id: 'limit',
    label: 'Limit',
    cell: row => <CellText title={row.limit.toLocaleString()} />,
  },
  {
    id: 'used',
    label: 'Used',
    cell: row => <CellText title={row.used.toLocaleString()} />,
  },
  {
    id: 'remaining',
    label: 'Remaining',
    cell: row => <CellText title={row.remaining.toLocaleString()} />,
  },
  {
    id: 'reset',
    label: 'Resets At',
    cell: row => <CellText title={formatResetTime(row.reset)} />,
  },
];

export interface GitHubRateLimitViewProps {
  data: RateLimitResponse | null;
  loading: boolean;
  error: string | null;
  onRefresh: () => void;
}

/**
 * Pure presentational view for the GitHub rate-limit card. All data and the
 * refresh callback are supplied via props, which keeps it trivially testable
 * with synthetic inputs and decouples it from the data-fetching hook.
 */
export const GitHubRateLimitView = ({
  data,
  loading,
  error,
  onRefresh,
}: GitHubRateLimitViewProps) => {
  const rows: RateLimitRow[] | undefined = data
    ? [
        ...RESOURCE_DISPLAY_ORDER.filter(key => key in data.resources).map(
          key => ({ id: key, name: key, ...data.resources[key] }),
        ),
        ...Object.entries(data.resources)
          .filter(([key]) => !RESOURCE_DISPLAY_ORDER.includes(key))
          .map(([key, resource]) => ({ id: key, name: key, ...resource })),
      ]
    : undefined;

  const { tableProps } = useTable<RateLimitRow>({
    mode: 'complete',
    data: rows,
    paginationOptions: { type: 'none' },
  });

  return (
    <Card>
      <CardHeader>
        <Flex justify="between" align="center" gap="3">
          <Flex direction="column" gap="1">
            <Text variant="title-medium">GitHub Rate Limit Status</Text>
            <Text variant="body-small" color="secondary">
              Current GitHub App installation token rate limit usage. Note that there seems to be a bug in the API that makes the `code_scanning_upload` rate match the `core` rate.
            </Text>
          </Flex>
          <Button
            variant="primary"
            size="medium"
            loading={loading}
            onPress={() => {
              onRefresh();
            }}
          >
            Refresh
          </Button>
        </Flex>
      </CardHeader>
      <CardBody>
        <Flex direction="column" gap="3">
          {error && (
            <Alert status="danger" title="Error" description={error} />
          )}

          <Table columnConfig={columns} {...tableProps} />
        </Flex>
      </CardBody>
    </Card>
  );
};

export const GitHubRateLimitCard = () => {
  const { data, loading, error, refresh } = useGitHubRateLimit();
  return (
    <GitHubRateLimitView
      data={data}
      loading={loading}
      error={error}
      onRefresh={refresh}
    />
  );
};
